import axios from "axios";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

apiClient.interceptors.response.use((response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.error("Unauthorized request. Redirecting to login...");
      localStorage.clear(); // Clear localStorage on 401
      window.location.href = "/"; // Redirect to login page
    }
    return Promise.reject(error);
  }
);

const FilePageService = {
  folder_list: (userData) => apiClient.post("/user/user_document/index", userData),
  show_user_document: (id, userData) => apiClient.post(`/user/user_document/show/${id}`, userData),
  history_list: (id, userData) => apiClient.post(`/user/user_document/history/${id}`, userData),
  invalid_reason_note_list: (id) => apiClient.get(`/user/user_document/history-list/${id}`),
  missing_document_list: (id, userData) => apiClient.post(`/user/user_document/missing-document-list/${id}`, userData),
  mark_history_as_read: (id) => apiClient.get(`/user/user_document/mark-history-as-read/${id}`),
  mark_history_all_as_read: (id) => apiClient.get(`/user/user_document/mark-history-all-as-read/${id}`),
  show_other_document: (id, userData) => apiClient.post(`/user/user_document/other-documents/${id}`, userData),
  speaker_document: (id, userData) => apiClient.post(`/user/user_document/speaker-documents/${id}`, userData),
  folder_info_update: (id, userData) => apiClient.post(`/user/user_document/update-folder-info/${id}`, userData),
  delete_document_file: (id) => apiClient.get(`/user/user_document/delete-document-file/${id}`),
  add_document_files: (id, userData) => apiClient.post(`/user/user_document/add-document-files/${id}`, userData),
  add_speaker_document_files: (id, userData) => apiClient.post(`/user/user_document/speaker/doc/add/${id}`, userData),
  add_missing_document: (id, userData) => apiClient.post(`/user/user_document/add-missing-document/${id}`, userData),
  update_document_files: (id, userData) => apiClient.post(`/user/user_document/update-document-files/${id}`, userData),
  validate_document_files: (id, userData) => apiClient.post(`/user/user_document/validate-document-files/${id}`, userData),
  invalidreson: (id) => apiClient.post(`/user/user_document/show-invalid-message/${id}`),
  sitestatusUpdate: (userData) => apiClient.post(`/acsmanager/files/sitestatus`, userData),
  update_document_status: (id, userData) => apiClient.post(`/user/user_document/update-document-status/${id}`, userData),
  document_file_notes: (id, userData) => apiClient.post(`/user/user_document/document-file/notes/${id}`, userData),

  // Product
  product_index: (userData) => apiClient.post("/admin/insurer-product/index", userData),
  product_list: () => apiClient.get("/user/products-list"),
  insurer_product_list: (userData) => apiClient.post("/user/insurer-products-list", userData),
  product_create: (userData) => apiClient.post(`/admin/insurer-product/create`, userData),
  product_show: (id, userData) => apiClient.post(`/admin/insurer-product/show/${id}`, userData),
  product_update: (id, userData) => apiClient.post(`/admin/insurer-product/update/${id}`, userData),
  product_destroy: (id) => apiClient.get(`/admin/insurer-product/destroy/${id}`),
  product_update_status: (id, userData) => apiClient.post(`/admin/insurer-product/update-status/${id}`, userData),
  guarantee_basis_required_status_update: (id, userData) => apiClient.post(`/admin/insurer-product/guarantee-basis/required-status-update/${id}`, userData),

  // My Product
  my_product_list: (userData) => apiClient.post("/user/insurer-products/index", userData),
  my_product_create: (userData) => apiClient.post(`/user/insurer-products`, userData),
  my_product_show: (id) => apiClient.get(`/user/insurer-products/${id}`),
  my_product_update: (id, userData) => apiClient.post(`/user/insurer-products/${id}`, userData),
  my_product_destroy: (id) => apiClient.delete(`/user/insurer-products/${id}`),
  my_product_update_status: (id, userData) => apiClient.post(`/user/insurer-products/update-status/${id}`, userData),

  // QR Basis
  qr_basis_index: (userData) => apiClient.post("/admin/qr-basis/index", userData),
  admin_qr_basis_list: () => apiClient.get("/admin/qr-basis/list"),
  user_qr_basis_list: () => apiClient.get("/user/qr-basis-list"),
  insurer_products_list: () => apiClient.get("/admin/qr-basis/insurer-products-list"),
  qr_basis_questions_list: () => apiClient.get("/admin/qr-basis/questions-list"),
  qr_basis_questionsList_page: (userData) => apiClient.post("/admin/qr-basis/questionsList", userData),
  user_questions_list: (userData) => apiClient.post("/user/questions-list", userData),
  qr_basis_create: (userData) => apiClient.post("/admin/qr-basis/create", userData),
  qr_basis_show: (id) => apiClient.get(`/admin/qr-basis/show/${id}`),
  qr_basis_destroy: (id) => apiClient.get(`/admin/qr-basis/destroy/${id}`),
  qr_basis_update_status: (id, userData) => apiClient.post(`/admin/qr-basis/update-status/${id}`, userData),
  qr_basis_update: (id, userData) => apiClient.post(`/admin/qr-basis/update/${id}`, userData),

  // Quotation Basis
  quotation_basis_index: (userData) => apiClient.post("/admin/quotation-basis/index", userData),
  quotation_basis_list: () => apiClient.get("/admin/quotation-basis/list"),
  risk_types_list: () => apiClient.get("/admin/risk-types"),
  logical_block_by_risk_type: (userData) => apiClient.post("/user/logical_block/by-risk-type", userData),
  quotation_basis_create: (userData) => apiClient.post("/admin/quotation-basis/create", userData),
  quotation_basis_show: (id) => apiClient.get(`/admin/quotation-basis/show/${id}`),
  quotation_basis_update: (id, userData) => apiClient.post(`/admin/quotation-basis/update/${id}`, userData),
  quotation_basis_destroy: (id) => apiClient.get(`/admin/quotation-basis/destroy/${id}`),
  quotation_basis_update_status: (id, userData) => apiClient.post(`/admin/quotation-basis/update-status/${id}`, userData),

  // Guarantee Basis
  guarantee_basis_index: (userData) => apiClient.post("/admin/guarantee-basis/index", userData),
  guarantee_basis_list: () => apiClient.get("/admin/guarantee-basis/list"),
  create_guarantee_basis: (userData) => apiClient.post("/admin/guarantee-basis/create", userData),
  create_guarantee_basis_with_product: (userData) => apiClient.post("/admin/guarantee-basis/with-product", userData),
  show_guarantee_basis: (id) => apiClient.get(`/admin/guarantee-basis/show/${id}`),
  edit_guarantee_basis: (id, userData) => apiClient.post(`/admin/guarantee-basis/update/${id}`, userData),
  delete_guarantee_basis: (id) => apiClient.get(`/admin/guarantee-basis/destroy/${id}`),
  guarantee_basis_status_update: (id, userData) => apiClient.post(`/admin/guarantee-basis/update-status/${id}`, userData),

  // Question Basis
  question_list: (userData) => apiClient.post("/admin/questions", userData),
  create_question: (userData) => apiClient.post("/admin/questions/store", userData),
  show_question: (id) => apiClient.get(`/admin/questions/show/${id}`),
  edit_question: (id, userData) => apiClient.post(`/admin/questions/update/${id}`, userData),
  delete_question: (id) => apiClient.delete(`/admin/questions/delete/${id}`),
  questions_status_update: (id, userData) => apiClient.post(`/admin/questions/status/update/${id}`, userData),
};

export default FilePageService;